import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {
  AbstractComponent,
  ConfirmationModalComponent,
  EventsListModalComponent,
  NavTabsComponent
} from "@components";
import {Country, EventItem, League, LeagueDetailsComponentType, LeagueStatus, Lookups, Organiser, TOAST_STATE} from "@model";
import {
  AuthenticationService,
  EventService,
  ExportService,
  LeagueService,
  LoaderService,
  RequestService,
  ToastService
} from "@services";
import {isLeagueBasicDataValid} from "@utility";
import {ORGANISER_EVENT_EDIT, ORGANISER_EVENTS_MANAGEMENT} from "../../../../../app-routing.module";

@Component({
  selector: 'app-admin-league-details',
  templateUrl: './admin-league-details.component.html',
  styleUrls: ['./admin-league-details.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})

export class AdminLeagueDetailsComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input() league: League | undefined;
  @Input() organisers: Organiser[] = [];
  @Input() userRole: String | undefined;
  @Input({required: true}) sports: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @ViewChild("nav") nav?: NavTabsComponent;

  currentLeague: League | undefined;
  componentType: LeagueDetailsComponentType = LeagueDetailsComponentType.ADD
  isLeagueInvalid: boolean = true;

  constructor(
    private route: ActivatedRoute,
    public translocoService: TranslocoService,
    private eventService: EventService,
    private leagueService: LeagueService,
    private modalService: NgbModal,
    private authService: AuthenticationService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private router: Router,
    private requestService: RequestService,
    private exportService: ExportService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToRouteDataChange();
    this.isLeagueInvalid = !isLeagueBasicDataValid(this.currentLeague).isValid;
  }

  changeDisabled(league: League | undefined): boolean {
    return league?.status !== LeagueStatus.INACTIVE && this.authService.isOrganiser();
  }

  leagueChange(league: League | undefined) {
    this.currentLeague = league;
    this.isLeagueInvalid = !isLeagueBasicDataValid(this.currentLeague).isValid;
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({type, league}: Data): void => {
        this.componentType = type ?? LeagueDetailsComponentType.ADD;
        if (league) {
          this.currentLeague = league;
        } else {
          this.currentLeague = new League();
          this.currentLeague.status = LeagueStatus.INACTIVE;
          this.componentType = LeagueDetailsComponentType.ADD
        }
        this.isLeagueInvalid = !isLeagueBasicDataValid(this.currentLeague).isValid;
        this.leagueService.setCurrentLeague(this.currentLeague);
      })
    )
  }

  goToEventDetails(eventItem: EventItem): void {
    if (eventItem && eventItem.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: eventItem.id}});
    }
  }

  goToEventManagement(eventItem: EventItem): void {
    if (eventItem && eventItem.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENTS_MANAGEMENT], {queryParams: {eventId: eventItem.id}});
    }
  }

  handleDropdownClick(event: Event): void {
    event.stopPropagation();
  }

  isCopyComponentType(): boolean {
    return this.componentType === LeagueDetailsComponentType.COPY;
  }

  disableEditing(): boolean {
    return this.isCopyComponentType() || this.isLeagueInvalid;
  }

  openEraseEventConfirmationDialog(eventItem: EventItem) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });

    modalRef.componentInstance.text = this.translocoService.translate("league.details.list.of.events.delete.modal.text");
    modalRef.componentInstance.title = this.translocoService.translate("league.details.list.of.events.delete.modal.title");

    modalRef.result.then((_) => {
      this.loaderService.startLoading();
      this.leagueService.removeEventFromLeague(eventItem.id!, this.league!.id!, () => {
        this.leagueService.fetchLeague(this.league!.id!).subscribe((legaue: League | undefined) => {
          this.loaderService.dismissLoading();
          this.currentLeague = legaue;
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("league.details.list.of.events.delete.successs"));
        });
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("league.details.list.of.events.delete.error"));
      });
    }, (_) => {
      /// ignore
    });
  }

  openEventsForLeagueList() {
    this.leagueService.availableEventsForLeague(this.currentLeague?.id).subscribe((eventItems: EventItem[]) => {
      if (eventItems && eventItems.length > 0) {
        const modalRef: NgbModalRef = this.modalService.open(EventsListModalComponent, {
          centered: true,
          windowClass: 'discount-list-modal'
        });
        modalRef.componentInstance.events = eventItems;
        modalRef.componentInstance.title = "league.details.list.of.events";
        modalRef.componentInstance.tableColumnName = "league.details.list.of.events.event.name"
        modalRef.componentInstance.raceCheckboxEnabled = false;

        modalRef.result.then((events: EventItem[]) => {
          if (this.currentLeague) {
            this.loaderService.startLoading();
            let league: League = {...this.currentLeague};
            league.events = events;
            this.leagueService.addEventsToLeague(league, () => {
              this.requestService.fetchNewRequests.next(true);
              this.leagueService.fetchLeague(this.league!.id!).subscribe((legaue: League | undefined) => {
                this.currentLeague = legaue;
                this.loaderService.dismissLoading();
                this.toastService.showToast(TOAST_STATE.success,
                  this.translocoService.translate("league.details.list.of.events.add.success"));
              });
            }, () => {
              this.loaderService.dismissLoading();
              this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("league.details.list.of.events.add.error"));
            });
          }
        }, (_) => {
          /// ignore
        });
      }
    });
  }

  downloadLeagueEvents(type: string) {
    this.exportService.downloadLeagueEvents(type, +this.route.snapshot.queryParams['leagueId']);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.leagueService.currentLeague.next(undefined);
  }
}
